import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ConfirmDialog from "components/dialog/ConfirmDialog";

const ConfirmContext = createContext(null);

/**
 * ConfirmProvider — mounts a single ConfirmDialog and exposes `useConfirm()`.
 * Mounted once in App.tsx.
 */
export function ConfirmProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  // Options outlive `isOpen` so the dialog keeps its content during the exit animation.
  const [options, setOptions] = useState({});
  const resolveRef = useRef(null);

  const settle = useCallback((result) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setIsOpen(false);
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback((nextOptions = {}) => {
    // One dialog at a time: a newer request cancels the pending one.
    if (resolveRef.current) resolveRef.current(false);
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setOptions(nextOptions);
      setIsOpen(true);
    });
  }, []);

  useEffect(
    () => () => {
      if (resolveRef.current) resolveRef.current(false);
    },
    []
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        isOpen={isOpen}
        title={options.title}
        message={options.message}
        confirmLabel={options.confirmLabel}
        cancelLabel={options.cancelLabel}
        tone={options.tone}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
}

/**
 * useConfirm — returns `confirm(options) => Promise<boolean>`.
 * Resolves `true` on confirm and `false` on Cancel/Esc/overlay/close.
 *
 * @example
 * const confirm = useConfirm();
 * if (await confirm({ title: "Delete Campus?", message: "This cannot be undone.", confirmLabel: "Delete", tone: "danger" })) {
 *   await deleteCampus();
 * }
 *
 * @returns {(options: {title: React.ReactNode, message?: React.ReactNode, confirmLabel?: string, cancelLabel?: string, tone?: "default"|"danger"}) => Promise<boolean>}
 */
export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error("useConfirm must be used within a <ConfirmProvider>.");
  }
  return confirm;
}

export default ConfirmProvider;
